import React from 'react'
// constants
import { Modals } from 'constants/index'
// constants
import { AVAILABLE_TO } from 'constants/security'
// components
import { DrawerModal } from 'components/UI'
import Form from './components/Form'
// view
import { Header } from 'components/Modals/PlanItems/styles'
import { Container } from 'components/Modals/ReportPermissionsModal/views'
// context
import { DataHubAPIContext } from 'containers/DataHub/layers/API'
// graphql
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// hooks
import { useApolloClient, useCurrentUser, useInitialValues, useModalData } from 'hooks/index'
// types
import { IPowerBiReport } from 'types/graphql'
// themes
import { CloseIcon } from 'components/UI/Icons'
// utils
import { get } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { getOptionByValue } from 'utils/form'
import { getMemberOrPositionLabel } from 'utils/systemProcess'

const PAPER_PROPS = { style: { overflow: 'hidden' } }

const ReportPermissionsModal: React.FC = () => {
  const user = useCurrentUser()

  const { client } = useApolloClient()

  const { updateReport } = React.useContext(DataHubAPIContext)

  const {
    hideModal,
    modalData: { isOpen, report }
  } = useModalData<{ report: IPowerBiReport }>({ modalType: Modals.REPORT_PERMISSIONS_MODAL })

  const getUserAndPositions = async (ids: string[]) => {
    try {
      const usersAndPositions = get(
        await client.query({
          query: USERS_OR_POSITIONS_QUERY,
          variables: { filter: { companyId: user.companyId, ids } }
        }),
        'data.usersOrPositions.data',
        []
      )

      return usersAndPositions.map((item) => ({
        ...item,
        label: getMemberOrPositionLabel(item),
        value: item.id
      }))
    } catch (e) {
      handleError(e, 'DashboardSettingsDrawer.getUserAndPositions')
    }
  }

  const getInitialValues = async () => {
    const availableTo = report?.availableTo?.availableToUserOrPositionIds || []
    const availableToIds = availableTo.filter((id: string) => id !== AVAILABLE_TO.ALL && id !== AVAILABLE_TO.MANAGERS)

    const userAndPositions = await getUserAndPositions(availableToIds)

    const options = [
      {
        label: '(All Users)',
        value: AVAILABLE_TO.ALL
      },
      { label: '(Managers)', value: AVAILABLE_TO.MANAGERS },
      ...(userAndPositions || [])
    ]

    return {
      availableTo: availableToIds.map((id) => getOptionByValue(id, options))
    }
  }

  const initialValues = useInitialValues(getInitialValues, report)

  const onFormSubmit = async (data) => {
    await updateReport(report.id, {
      availableTo: {
        availableToUserOrPositionIds: data.availableTo?.map((item) => item.value)
      }
    })

    hideModal()
  }

  return (
    <DrawerModal width='500px' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Report Access</span>
        <CloseIcon onClick={hideModal} />
      </Header>
      <Container>
        <Form initialValues={initialValues} onSubmit={onFormSubmit} hideModal={hideModal} />
      </Container>
    </DrawerModal>
  )
}

export default ReportPermissionsModal
